import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// List (paginated)
export const getCounterSales = createAsyncThunk(
  "counterSale/getCounterSales",
  async (payload = {}, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(payload)) {
        if (value === null || value === undefined || value === "") continue;
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, v));
        } else {
          params.append(key, value);
        }
      }
      const { data } = await axios.get(
        `${API_URL.COUNTER_SALE.SLUG}?${params.toString()}`,
      );
      return {
        list: data.data.list,
        totalRecords: data.data.totalRecords,
      };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Detail
export const getCounterSaleById = createAsyncThunk(
  "counterSale/getCounterSaleById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.COUNTER_SALE.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Preview next CounterSale number
export const getNextCounterSaleNumber = createAsyncThunk(
  "counterSale/getNextCounterSaleNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.COUNTER_SALE.NEXT_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Save as Draft (create or update existing draft)
export const saveCounterSaleDraft = createAsyncThunk(
  "counterSale/saveCounterSaleDraft",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.COUNTER_SALE.DRAFT, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Create and Post in one shot
export const createAndPostCounterSale = createAsyncThunk(
  "counterSale/createAndPostCounterSale",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.COUNTER_SALE.SLUG, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Post an existing Draft
export const postCounterSale = createAsyncThunk(
  "counterSale/postCounterSale",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.COUNTER_SALE.POST(id));
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Update a Posted (pre-IRN) Counter Sale with stock-delta reconciliation
export const updatePostedCounterSale = createAsyncThunk(
  "counterSale/updatePostedCounterSale",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        API_URL.COUNTER_SALE.UPDATE_POSTED(id),
        payload,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Cancel (Draft → Cancelled soft OR Posted → Cancelled with stock reversal)
export const cancelCounterSale = createAsyncThunk(
  "counterSale/cancelCounterSale",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.COUNTER_SALE.CANCEL(id),
        payload,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Delete a Draft
export const deleteCounterSaleDraft = createAsyncThunk(
  "counterSale/deleteCounterSaleDraft",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.COUNTER_SALE.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Mirrors SalesOrder's resolveDiscountSchemes — hits the same endpoint with
// the same payload shape (customerId + list of {itemId, quantity}) and
// returns per-item {itemId, discountSchemeId, discountSchemeName,
// discountPercentage}. Used by the CS form to auto-apply scheme discounts
// when (customerId is present && status is Draft). Result is fed back into
// the items table; no slice state is mutated.
export const resolveCounterSaleDiscountSchemes = createAsyncThunk(
  "counterSale/resolveDiscountSchemes",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.DISCOUNT_SCHEME.RESOLVE,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Loads the active Payment Terms list for the CS form's required dropdown.
// Mirrors salesOrderSlice.getPaymentTerms — same endpoint + paging contract.
// Kept CS-local (not shared) to keep the counterSale slice self-contained;
// matches the pattern we used for resolveCounterSaleDiscountSchemes.
export const getCounterSalePaymentTerms = createAsyncThunk(
  "counterSale/getPaymentTerms",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("PageNumber", payload.PageNumber || 1);
      params.append("PageSize", payload.PageSize || 50);
      if (payload.SearchText) {
        params.append("SearchText", payload.SearchText);
      }
      const { data } = await axios.get(
        `${API_URL.PAYMENT_TERMS.ACTIVE}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  counter_sale_list: [],
  total_records: 0,
  counter_sale_detail: null,
  next_number: "",
  current_values: {},
  active_payload: {},
  // Active Payment Terms list, populated by getCounterSalePaymentTerms on
  // the form's initial load. Each row: { id, name, days }.
  payment_terms_list: [],
  loading: false,
  error: null,
};

const counterSaleSlice = createSlice({
  name: "counterSale",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    clearCounterSaleDetail(state) {
      state.counter_sale_detail = null;
    },
    resetCounterSaleState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCounterSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCounterSales.fulfilled, (state, action) => {
        state.counter_sale_list = action.payload.list || [];
        state.total_records = action.payload.totalRecords || 0;
        state.loading = false;
      })
      .addCase(getCounterSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getCounterSaleById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCounterSaleById.fulfilled, (state, action) => {
        state.counter_sale_detail = action.payload;
        state.loading = false;
      })
      .addCase(getCounterSaleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getNextCounterSaleNumber.pending, (state) => {
        // Force a "" → value transition every time the thunk runs so the form's
        // seeding effect re-fires after resetFields() even when the BE returns
        // the same suggestion as the prior visit (string ref unchanged would
        // otherwise be a no-op for useSelector).
        state.next_number = "";
      })
      .addCase(getNextCounterSaleNumber.fulfilled, (state, action) => {
        state.next_number = action.payload;
      })
      .addCase(getCounterSalePaymentTerms.fulfilled, (state, action) => {
        state.payment_terms_list = action.payload?.list || action.payload || [];
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  clearCounterSaleDetail,
  resetCounterSaleState,
} = counterSaleSlice.actions;

export default counterSaleSlice.reducer;
